import {
    createEmptyAnnouncement,
    DEFAULT_FALLBACK,
    NbaAnnouncement,
    NbaAnnouncementStatus,
    NbaAudience,
    NbaFallbackMessage,
    NbaOverrideMode,
    NbaPublishMode
} from '../models/next-best-action.model';

export interface DefaultMessageApi {
    title: string;
    description: string;
    button_text: string;
    button_action: string;
    link: string;
}

export interface MappedAnnouncementState {
    id: string | null;
    announcement: NbaAnnouncement;
    status: NbaAnnouncementStatus;
    fallback?: NbaFallbackMessage;
}

interface NormalizedAnnouncementRecord {
    id: string | null;
    audience: string;
    status: string | null;
    state: string | null;
    publish_type: string | null;
    title: string;
    description: string;
    button_text: string;
    button_action: string | null;
    link: string | null;
    popup_message: string | null;
    scheduled_at: string | null;
    expires_at: string | null;
}

interface JsonApiResource {
    id?: string | null;
    type?: string;
    attributes?: Record<string, unknown>;
    default_message?: DefaultMessageApi;
}

const AUDIENCE_TO_API: Record<NbaAudience, string> = {
    all: 'all_users',
    brightside: 'brightside_led',
    partner: 'partner_led'
};

const AUDIENCE_FROM_API: Record<string, NbaAudience> = {
    all_users: 'all',
    brightside_led: 'brightside',
    partner_led: 'partner'
};

export function mapAnnouncementsResponse(response: unknown): MappedAnnouncementState | null {
    const records = extractAnnouncementRecords(response);
    const fallback = extractDefaultMessage(response);
    const record = pickAnnouncementRecord(records);

    if (!record && !fallback) {
        return null;
    }

    if (!record) {
        return {
            id: null,
            announcement: createEmptyAnnouncement('all'),
            status: 'none',
            ...(fallback ? { fallback } : {})
        };
    }

    const audience = AUDIENCE_FROM_API[record.audience] ?? 'all';

    return {
        id: record.id,
        announcement: mapApiAnnouncement(record, audience),
        status: deriveAnnouncementStatus(record),
        ...(fallback ? { fallback } : {})
    };
}

export function toAnnouncementRequestBody(
    announcement: NbaAnnouncement,
    publishMode: NbaPublishMode,
    id?: string | null
): { data: Record<string, unknown> } {
    const attributes: Record<string, unknown> = {
        audience: AUDIENCE_TO_API[announcement.audience],
        title: announcement.title,
        description: announcement.description,
        button_text: announcement.buttonText,
        button_action: announcement.mode === 'link' ? 'redirect' : 'popup',
        publish_type: publishMode === 'now' ? 'publish_now' : 'schedule',
        link: null,
        popup_message: null,
        scheduled_at: null,
        expires_at: toIsoDateTime(announcement.endDate, announcement.endTime)
    };

    if (announcement.mode === 'link') {
        attributes['link'] = announcement.link;
    } else {
        attributes['popup_message'] = announcement.popupMessage;
    }

    if (publishMode === 'schedule') {
        attributes['scheduled_at'] = toIsoDateTime(announcement.startDate, announcement.startTime);
    }

    const data: Record<string, unknown> = {
        type: 'mentor_hub_announcements',
        attributes
    };

    if (id) {
        data['id'] = id;
    }

    return { data };
}

export function toDefaultMessageRequestBody(
    fallback: NbaFallbackMessage
): { default_message: DefaultMessageApi } {
    return {
        default_message: {
            title: fallback.title,
            description: fallback.description,
            button_text: fallback.buttonText,
            button_action: 'redirect',
            link: fallback.link
        }
    };
}

function extractAnnouncementRecords(response: unknown): NormalizedAnnouncementRecord[] {
    if (!response || typeof response !== 'object') {
        return [];
    }

    const payload = response as Record<string, unknown>;

    if (Array.isArray(payload['mentor_hub_announcements'])) {
        return payload['mentor_hub_announcements'].map((record) =>
            normalizeFlatRecord(record as Record<string, unknown>)
        );
    }

    if (payload['mentor_hub_announcement']) {
        return [normalizeFlatRecord(payload['mentor_hub_announcement'] as Record<string, unknown>)];
    }

    if (Array.isArray(payload['data'])) {
        return payload['data'].map((record) => normalizeJsonApiResource(record as JsonApiResource));
    }

    if (payload['data'] && typeof payload['data'] === 'object') {
        const data = payload['data'] as JsonApiResource;
        if (data.attributes || data.id) {
            return [normalizeJsonApiResource(data)];
        }
    }

    return [];
}

function extractDefaultMessage(response: unknown): NbaFallbackMessage | null {
    if (!response || typeof response !== 'object') {
        return null;
    }

    const payload = response as Record<string, unknown>;

    if (payload['default_message']) {
        return mapDefaultMessage(payload['default_message'] as DefaultMessageApi);
    }

    const data = payload['data'];
    if (data && typeof data === 'object' && 'default_message' in data) {
        return mapDefaultMessage((data as JsonApiResource).default_message);
    }

    return null;
}

function pickAnnouncementRecord(records: NormalizedAnnouncementRecord[]): NormalizedAnnouncementRecord | null {
    if (!records.length) {
        return null;
    }

    const active = records.find((record) => deriveAnnouncementStatus(record) !== 'none');
    if (active) {
        return active;
    }

    const populated = records.find((record) => record.id || record.title?.trim());
    return populated ?? null;
}

function normalizeFlatRecord(record: Record<string, unknown>): NormalizedAnnouncementRecord {
    return {
        id: (record['id'] as string | null) ?? null,
        audience: String(record['audience'] ?? ''),
        status: (record['status'] as string | null) ?? null,
        state: (record['state'] as string | null) ?? null,
        publish_type: (record['publish_type'] as string | null) ?? null,
        title: String(record['title'] ?? ''),
        description: String(record['description'] ?? ''),
        button_text: String(record['button_text'] ?? ''),
        button_action: (record['button_action'] as string | null) ?? null,
        link: (record['link'] as string | null) ?? null,
        popup_message: (record['popup_message'] as string | null) ?? null,
        scheduled_at: (record['scheduled_at'] as string | null) ?? null,
        expires_at: (record['expires_at'] as string | null) ?? null
    };
}

function normalizeJsonApiResource(resource: JsonApiResource): NormalizedAnnouncementRecord {
    const attributes = resource.attributes ?? {};

    return {
        id: resource.id ?? null,
        audience: String(attributes['audience'] ?? ''),
        status: (attributes['status'] as string | null) ?? null,
        state: (attributes['state'] as string | null) ?? null,
        publish_type: (attributes['publish_type'] as string | null) ?? null,
        title: String(attributes['title'] ?? ''),
        description: String(attributes['description'] ?? ''),
        button_text: String(attributes['button_text'] ?? ''),
        button_action: (attributes['button_action'] as string | null) ?? null,
        link: (attributes['link'] as string | null) ?? null,
        popup_message: (attributes['popup_message'] as string | null) ?? null,
        scheduled_at: (attributes['scheduled_at'] as string | null) ?? null,
        expires_at: (attributes['expires_at'] as string | null) ?? null
    };
}

function mapApiAnnouncement(record: NormalizedAnnouncementRecord, audience: NbaAudience): NbaAnnouncement {
    const mode: NbaOverrideMode = record.button_action === 'popup' ? 'popup' : 'link';
    const publishMode: NbaPublishMode = record.publish_type === 'schedule' ? 'schedule' : 'now';
    const scheduled = splitIsoDateTime(record.scheduled_at);
    const expires = splitIsoDateTime(record.expires_at);

    return {
        audience,
        title: record.title ?? '',
        description: record.description ?? '',
        buttonText: record.button_text ?? '',
        mode,
        link: record.link ?? '',
        popupMessage: record.popup_message ?? '',
        publishMode,
        startDate: scheduled.date,
        startTime: scheduled.time,
        endDate: expires.date,
        endTime: expires.time
    };
}

function mapDefaultMessage(message?: DefaultMessageApi): NbaFallbackMessage {
    if (!message) {
        return { ...DEFAULT_FALLBACK };
    }

    return {
        title: message.title ?? DEFAULT_FALLBACK.title,
        description: message.description ?? DEFAULT_FALLBACK.description,
        buttonText: message.button_text ?? DEFAULT_FALLBACK.buttonText,
        link: message.link ?? DEFAULT_FALLBACK.link
    };
}

function deriveAnnouncementStatus(record: NormalizedAnnouncementRecord): NbaAnnouncementStatus {
    const now = new Date();
    const lifecycleState = record.state ?? record.status;

    if (lifecycleState === 'inactive' || lifecycleState === 'draft' || !record.title?.trim()) {
        return 'none';
    }

    if (record.expires_at && new Date(record.expires_at) <= now) {
        return 'none';
    }

    if (lifecycleState === 'scheduled') {
        return 'scheduled';
    }

    if (lifecycleState === 'published' || lifecycleState === 'active' || lifecycleState === 'live') {
        return 'live';
    }

    if (record.publish_type === 'schedule' && record.scheduled_at) {
        return new Date(record.scheduled_at) > now ? 'scheduled' : 'live';
    }

    if (record.publish_type === 'publish_now' && record.title?.trim()) {
        return 'live';
    }

    return 'none';
}

function toIsoDateTime(date: string, time: string): string | null {
    if (!date?.trim() || !time?.trim()) {
        return null;
    }

    const [day, month, year] = date.split('/').map((part) => part.trim());
    if (!day || !month || !year) {
        return null;
    }

    const normalizedTime = time.trim().length === 5 ? `${time.trim()}:00` : time.trim();
    return new Date(`${year}-${month}-${day}T${normalizedTime}`).toISOString();
}

function splitIsoDateTime(value?: string | null): { date: string; time: string } {
    if (!value) {
        return { date: '', time: '' };
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return { date: '', time: '' };
    }

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');

    return {
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}`
    };
}
